// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The one Filter menu's own pieces (Figma November 2026, frame "0b Filter menu
// open"): Group by, the Status boxes, Show minor and Reset, and the count on
// its button.
import { Button, Checkbox, RadioGroup, Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { type PinGroupBy } from "./pinGroups";
import {
  isReviewCommentFilterActive,
  REVIEW_COMMENT_TAG_IDS,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import { type PinViewMode } from "./useAskV2ReviewPinView";
import {
  type ReviewListGroupBy,
  type ReviewListStatusFilter,
  statusFilterNarrows,
} from "./useReviewListView";

import {
  REVIEW_COMMENT_GROUPS,
  type ReviewCommentGroup,
} from "../../utils/visibleReviewComments";

const STATUS_LABEL_KEYS: Record<ReviewCommentGroup, string> = {
  unresolved: "askV2.review.sectionUnresolved",
  confirmed: "askV2.review.sectionConfirmed",
  rejected: "askV2.review.sectionRejected",
};

export type ReviewListGroupByControl = {
  value: ReviewListGroupBy;
  onChange: (groupBy: ReviewListGroupBy) => void;
};

/** The pin view's groups, offered beside the list's own in one Group by. */
export type ReviewListPinGrouping = {
  mode: PinViewMode;
  groupBy: PinGroupBy;
  onModeChange: (mode: PinViewMode) => void;
  onGroupByChange: (groupBy: PinGroupBy) => void;
};

type GroupByOption = ReviewListGroupBy | PinGroupBy;

const LIST_GROUP_BY_OPTIONS: readonly {
  value: ReviewListGroupBy;
  labelKey: string;
}[] = [
  { value: "none", labelKey: "askV2.review.groupByNone" },
  { value: "status", labelKey: "askV2.review.groupByStatus" },
];

const PIN_GROUP_BY_OPTIONS: readonly { value: PinGroupBy; labelKey: string }[] =
  [
    { value: "block", labelKey: "askV2.review.pinView.byBlock" },
    { value: "place", labelKey: "askV2.review.pinView.byPlace" },
  ];

function isPinGroupBy(value: GroupByOption): value is PinGroupBy {
  return value === "block" || value === "place";
}

export type ReviewListShowMinorControl = {
  value: boolean;
  onChange: (showMinor: boolean) => void;
};

/**
 * How many boxes hide something: each unticked status, type, Always-on,
 * raiser, and Show minor. Boxes that narrow nothing (none ticked) count 0.
 */
export function activeFilterCount({
  activeTags,
  alwaysOn,
  hiddenSources,
  sourceKeys,
  statuses,
  showMinor,
}: {
  activeTags: ReadonlySet<ReviewCommentTagId>;
  alwaysOn: boolean;
  hiddenSources: ReadonlySet<ReviewCommentSourceKey>;
  sourceKeys: readonly ReviewCommentSourceKey[];
  statuses?: ReadonlySet<ReviewCommentGroup>;
  showMinor?: boolean;
}): number {
  let count = 0;
  if (
    isReviewCommentFilterActive(activeTags, alwaysOn, hiddenSources, sourceKeys)
  ) {
    count += REVIEW_COMMENT_TAG_IDS.filter(
      (tag) => !activeTags.has(tag),
    ).length;
    if (!alwaysOn) count += 1;
    count += sourceKeys.filter((key) => hiddenSources.has(key)).length;
  }
  if (statuses && statusFilterNarrows(statuses)) {
    count += REVIEW_COMMENT_GROUPS.length - statuses.size;
  }
  if (showMinor === false) count += 1;
  return count;
}

/**
 * Why the list is empty, which is not always "you filtered it". A review that
 * found nothing -- or one whose checks were all blocked -- used to read "No
 * findings match the selected types" while every type was still selected. A
 * rejection never empties it: rejected comments stay in the list, grayed.
 */
export function reviewListEmptyState({
  activeTags,
  alwaysOn,
  hiddenSourceCount,
  statusNarrowed,
  changedOnly = false,
}: {
  activeTags: ReadonlySet<ReviewCommentTagId>;
  alwaysOn: boolean;
  hiddenSourceCount: number;
  statusNarrowed: boolean;
  /** The list is cut to the comments inside a changed window. */
  changedOnly?: boolean;
}): { testId: string; copyKey: string } {
  const narrows =
    !alwaysOn ||
    REVIEW_COMMENT_TAG_IDS.some((tag) => !activeTags.has(tag)) ||
    hiddenSourceCount > 0 ||
    statusNarrowed;
  if (narrows) {
    return {
      testId: "ask-v2-review-tag-filter-empty",
      copyKey: "askV2.review.tagFilterEmpty",
    };
  }
  // why: the cut can empty a review that has findings; "no findings" would deny them.
  return changedOnly
    ? {
        testId: "ask-v2-review-changed-only-empty",
        copyKey: "askV2.review.drawer.changedOnly.empty",
      }
    : {
        testId: "ask-v2-review-no-findings-empty",
        copyKey: "askV2.review.noFindings",
      };
}

export function MenuHeading({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) {
  return (
    <Text
      as="p"
      id={id}
      className="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.44px] text-puntt-neutral-gray-11"
    >
      {children}
    </Text>
  );
}

const ROW =
  "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2";

/**
 * One Group by for the list and the pins: No grouping and Status draw every
 * pin flat; a block or place groups the pins and leaves the list ungrouped.
 */
export function AskV2ReviewGroupBySection({
  groupBy,
  pinGrouping,
}: {
  groupBy: ReviewListGroupByControl;
  pinGrouping?: ReviewListPinGrouping;
}) {
  const { t } = useTranslation("ui");
  const options = pinGrouping
    ? [...LIST_GROUP_BY_OPTIONS, ...PIN_GROUP_BY_OPTIONS]
    : LIST_GROUP_BY_OPTIONS;
  const value: GroupByOption =
    pinGrouping?.mode === "groups" ? pinGrouping.groupBy : groupBy.value;
  const choose = (next: GroupByOption) => {
    if (isPinGroupBy(next)) {
      pinGrouping?.onGroupByChange(next);
      pinGrouping?.onModeChange("groups");
      // why: the pin groups replace the list's sections, so a Status grouping
      // left behind would come back unasked when she picks No grouping later.
      groupBy.onChange("none");
      return;
    }
    pinGrouping?.onModeChange("flat");
    groupBy.onChange(next);
  };
  return (
    <section
      className="mb-2 border-b border-puntt-neutral-gray-4 px-2 pb-2"
      data-testid="ask-v2-review-group-by"
      aria-labelledby="ask-v2-review-group-by-heading"
    >
      <MenuHeading id="ask-v2-review-group-by-heading">
        {t("askV2.review.groupByHeading")}
      </MenuHeading>
      <RadioGroup.Root
        size="1"
        value={value}
        onValueChange={(next) => choose(next as GroupByOption)}
        aria-labelledby="ask-v2-review-group-by-heading"
      >
        {options.map((option) => (
          <Text
            key={option.value}
            as="label"
            size="2"
            className="flex cursor-pointer items-center gap-2 py-1"
          >
            <RadioGroup.Item
              value={option.value}
              data-testid={`ask-v2-review-group-by-${option.value}`}
            />
            {t(option.labelKey)}
          </Text>
        ))}
      </RadioGroup.Root>
    </section>
  );
}

export function AskV2ReviewStatusFilterSection({
  statusFilter,
}: {
  statusFilter: ReviewListStatusFilter;
}) {
  const { t } = useTranslation("ui");
  return (
    <section
      className="mb-2 border-b border-puntt-neutral-gray-4 pb-2"
      data-testid="ask-v2-review-status-filter"
      aria-labelledby="ask-v2-review-status-filter-heading"
    >
      <MenuHeading id="ask-v2-review-status-filter-heading">
        {t("askV2.review.groupByStatus")}
      </MenuHeading>
      <ul className="space-y-1">
        {REVIEW_COMMENT_GROUPS.map((status) => (
          <li key={status}>
            <label
              className={ROW}
              data-testid={`ask-v2-review-status-filter-${status}`}
            >
              <Checkbox
                checked={statusFilter.shown.has(status)}
                onCheckedChange={(value) =>
                  statusFilter.onToggle(status, value === true)
                }
              />
              <Text size="2">{t(STATUS_LABEL_KEYS[status])}</Text>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AskV2ReviewFilterMenuFooter({
  showMinor,
  canReset,
  onReset,
}: {
  showMinor?: ReviewListShowMinorControl;
  canReset: boolean;
  onReset: () => void;
}) {
  const { t } = useTranslation("ui");
  return (
    <div className="mt-2 border-t border-puntt-neutral-gray-4 pt-2">
      {showMinor ? (
        <label className={ROW} data-testid="ask-v2-review-filter-show-minor">
          <Checkbox
            checked={showMinor.value}
            onCheckedChange={(value) => showMinor.onChange(value === true)}
          />
          <Text size="2">{t("askV2.review.pinView.showMinor")}</Text>
        </label>
      ) : null}
      <Button
        type="button"
        size="1"
        variant="soft"
        color="gray"
        className="mx-2 mt-1"
        disabled={!canReset}
        onClick={onReset}
        data-testid="ask-v2-review-filter-reset"
      >
        {t("askV2.review.filterReset")}
      </Button>
    </div>
  );
}
